import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  gql,
  useQuery,
  usePaginationHelper,
  useLazyQuery,
} from '@common/hooks';
import { Row, Col, Space, Empty } from 'antd';
import { Colors, FontSizes, Spacing } from 'works/legacy/lib/style-helper';
import moment from 'moment';
import { CandidateLink } from 'works/components/candidate-link';
import {
  buildOptionsFromData,
  FilteredTable,
} from 'works/components/filtered-table';
import { MultiSelectTableFilter } from 'works/components/filtered-table/multi-select-table-filter';
import { AssignmentStateMenu } from 'works/components/assignment-state-menu';
import { AssignmentCancelReason } from '@working-experience/components';
import { ExpiredComplianceItemsCount } from 'works/components/expired-compliance-items-count';
import { ExtensionOfferState } from 'works/components/extension-offer-state';
import { PaginatedStaffingRequestsAssignmentsQuery } from 'works/graphql/staffing-request-assignment';
import {
  ButtonGroup,
  Currency,
  DateRangeFilter,
  EmptyContent,
  ErrorHandler,
  NumberRangeFilter,
  Portal,
  SelectColumnsDropdown,
  selectColumnsStore,
  TextFilterSearch,
} from '@common/components';
import { ExtensionOffersApprovalsPendingList } from './extension-offer-approvals-pending-list';
import { ExportAssignmentsButton } from 'works/components/export-team-button';
import { useTeamExportButton } from 'works/components/export-team-button/export-team-button-context';
import { RolesQuery } from '@clinician-profile/graphql';
import { RenderShiftType } from '@applicant-flow/components';
import { titleize } from '@common/services';
import { ENUM_QUERY } from '@common/graphql';
import { handleEmploymentTypeFilter } from '@clinician-profile/services';
import {
  WORKS_CLINICAL_UNITS_QUERY,
  ORGANIZATION_BUSINESSES_QUERY,
} from '@institutions/graphql';

const WORKS_SUPPLIERS = gql`
  query Suppliers {
    suppliers {
      id
      name
    }
  }
`;

const LOCAL_STORAGE_KEY = 'ClientAdminTeamTable';

export function LongTermTeamTable({ showHeader = false, triggerPortal }) {
  const { enableExport } = useTeamExportButton();

  const {
    filters,
    hasAnyFilter,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: LOCAL_STORAGE_KEY,
    defaultPaginationData: {
      defaultPageSize: 10,
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'STATUS',
    },
    columnsDictionary: {
      name: 'NAME',
      startDate: 'START',
      endDate: 'END',
      state: 'STATUS',
      costCenter: 'COST_CENTER',
      rate: 'RATE',
      supplierName: 'SUPPLIER_NAME',
      shift: 'SHIFT',
      hiringLeader: 'HIRING_LEADER',
      code: 'CODE',
      clinicalUnit: 'CLINICAL_UNIT',
      externalId: 'EXTERNAL_ID',
      employmentType: 'EMPLOYMENT_TYPE',
    },
  });

  const [fetchOrgBusiness, { error: orgBusinesserror, data: orgBusinessData }] =
    useLazyQuery(ORGANIZATION_BUSINESSES_QUERY);

  const [fetchSupplier, { error: supplierError, data: supplierData }] =
    useLazyQuery(WORKS_SUPPLIERS);

  const [fetchRoles, { error: rolesError, data: rolesData }] =
    useLazyQuery(RolesQuery);

  const [
    fetchEmploymentTypes,
    { error: employmentTypesError, data: employmentTypesData },
  ] = useLazyQuery(ENUM_QUERY, { variables: { key: 'EmploymentTypes' } });

  const [
    fetchClinicalUnits,
    { error: clinicalUnitError, data: clinicalUnitData },
  ] = useLazyQuery(WORKS_CLINICAL_UNITS_QUERY);

  const { error, data, loading } = useQuery(
    PaginatedStaffingRequestsAssignmentsQuery,
    {
      variables: {
        ...paginationVariables,
        staffingRequestTypes: ['long_term'],
      },
      notifyOnNetworkStatusChange: true,
      onCompleted() {
        fetchOrgBusiness();
        fetchSupplier();
        fetchRoles();
        fetchClinicalUnits();
        fetchEmploymentTypes();
      },
    }
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'worksProfile',
      key: 'name',
      sorter: true,
      filtered: filters.name || false,
      filteredValue: filters.name || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderTeamMemberLink(worksProfile, record) {
        return (
          <>
            <CandidateLink
              firstName={worksProfile.firstName}
              lastName={worksProfile.lastName}
              link={`/works/team/long_term/${record.id}`}
              subText={<ExpiredComplianceItemsCount assignment={record} />}
            />
          </>
        );
      },
    },
    {
      title: 'EEID',
      dataIndex: 'employeeId',
      key: 'employeeId',
      sorter: true,
      render: (_, record) => <>{record.organizationProfile?.employeeId}</>,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplier',
      sorter: true,
      filtered: filters.supplier || false,
      filteredValue: filters.supplier || null,
      filterDropdown: MultiSelectTableFilter,
      filters: supplierData && buildOptionsFromData(supplierData.suppliers),
      render: (_, record) => <>{record.supplier.name}</>,
    },
    {
      title: 'External Job ID',
      dataIndex: 'externalId',
      key: 'externalId',
      sorter: true,
      filtered: filters.externalId || false,
      filteredValue: filters.externalId || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (_, record) => <>{record.staffingRequest.externalId}</>,
    },
    {
      title: 'Manager',
      dataIndex: 'hiringLeader',
      key: 'hiringLeader',
      sorter: true,
      filtered: filters.hiringLeader || false,
      filteredValue: filters.hiringLeader || null,
      className: 'sentry-mask',
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (_, record) => <>{record.hiringLeader.name}</>,
    },
    {
      title: 'CC',
      dataIndex: 'organizationUnit',
      key: 'costCenter',
      sorter: true,
      filtered: filters.costCenter || false,
      filteredValue: filters.costCenter || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderCostCenter(_, record) {
        return record.organizationUnit.costCenter;
      },
    },
    {
      title: 'Facility',
      dataIndex: 'organizationBusiness',
      key: 'organizationBusiness',
      filtered: filters.organizationBusiness || false,
      filteredValue: filters.organizationBusiness || null,
      filters: buildOptionsFromData(
        orgBusinessData?.organizationBusinesses,
        (organizationBusiness) => ({
          value: organizationBusiness.id,
          text: organizationBusiness.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: function RenderFacilityCityState(_, record) {
        let organizationBusiness = record.organizationUnit.organizationBusiness;
        return (
          <>
            {organizationBusiness.name}
            <br />
            <span className="secondary-paragraph">
              {organizationBusiness.city}, {organizationBusiness.stateCode}
            </span>
          </>
        );
      },
      sorter: true,
    },
    {
      title: 'Unit',
      dataIndex: 'organizationUnit',
      key: 'clinicalUnit',
      sorter: true,
      filtered: filters.clinicalUnit || false,
      filteredValue: filters.clinicalUnit || null,
      filters:
        clinicalUnitData &&
        buildOptionsFromData(
          clinicalUnitData.worksClinicalUnits,
          (worksClinicalUnit) => ({
            value: worksClinicalUnit.id,
            text: worksClinicalUnit.displayName,
          })
        ),
      filterDropdown: MultiSelectTableFilter,
      render: function renderClinicalUnit(_, record) {
        return <>{record.organizationUnit.clinicalUnit.displayName}</>;
      },
    },
    {
      title: 'Role',
      dataIndex: ['staffingRequest', 'role', 'abbreviation'],
      key: 'role',
      sorter: true,
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(rolesData?.roles, (role) => ({
        text: role.abbreviation,
        value: role.id,
      })),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Shift Type',
      dataIndex: 'staffingRequestAssignment',
      key: 'shiftType',
      width: '100px',
      sorter: true,
      render: (_, record) => (
        <>
          <RenderShiftType shiftType={record.shiftType} />
        </>
      ),
    },
    {
      title: 'Shift',
      dataIndex: 'staffingRequestAssignment',
      key: 'shift',
      sorter: true,
      render: (_, record) => (
        <>{`${record.shiftsPerWeek}x${record.hoursPerShift}`}</>
      ),
    },
    {
      title: 'Rate',
      dataIndex: 'billingRegularRate',
      key: 'rate',
      sorter: true,
      filterFormat: 'currencyRange',
      filtered: filters.rate,
      filteredValue: filters.rate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <NumberRangeFilter
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (rate) => <Currency value={rate} />,
    },
    {
      title: 'Employment',
      dataIndex: 'staffingRequestAssignment',
      key: 'employmentType',
      sorter: true,
      filtered: filters.employmentType || false,
      filteredValue: filters.employmentType || null,
      filters: handleEmploymentTypeFilter(employmentTypesData),
      filterDropdown: MultiSelectTableFilter,
      render: (_, record) => <>{titleize(record.employmentType)}</>,
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: true,
      filterFormat: 'dateRange',
      filtered: filters.startDate || false,
      filteredValue: filters.startDate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderStartDate(_, assignment) {
        const displayDate = moment(assignment.startDate);
        return displayDate.format('MM/DD/YYYY');
      },
    },
    {
      title: 'End',
      dataIndex: 'finalEndDate',
      sorter: true,
      key: 'endDate',
      filterFormat: 'dateRange',
      filtered: filters.endDate || false,
      filteredValue: filters.endDate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderEndDate(finalEndDate, assignment) {
        const hasStarted = !assignment.neverStarted;
        const isCancelled = assignment.state === 'cancelled';
        const color = hasStarted && isCancelled ? Colors.coral : 'inherit';

        const displayDate = moment(finalEndDate);
        return (
          <span style={{ color }}>{displayDate.format('MM/DD/YYYY')}</span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'staffingRequestAssignment',
      key: 'state',
      sorter: true,
      defaultSortOrder: 'ascend',
      filtered: filters.state || false,
      filteredValue: filters.state || null,
      filters: [
        {
          text: 'Onboarding',
          value: 'onboarding',
        },
        {
          text: 'Cleared',
          value: 'cleared',
        },
        {
          text: 'Working',
          value: 'working',
        },
        {
          text: 'Completed',
          value: 'completed',
        },
        {
          text: 'Cancelled',
          value: 'cancelled',
        },
        {
          text: 'Extension Pending Approval',
          value: 'extension_pending',
        },
        {
          text: 'Extension Sent',
          value: 'extension_sent',
        },
        {
          text: 'Extended',
          value: 'extended',
        },
        {
          text: 'Extension Change Requested',
          value: 'extension_change_requested',
        },
      ],
      render: function renderStatus(state, assignment) {
        return (
          <>
            <Space size={4}>
              <AssignmentStateMenu
                assignment={assignment}
                fontSize={FontSizes.small}
              />
              <ExtensionOfferState
                assignment={assignment}
                offer={assignment.activeExtensionOffer}
                extensionCount={assignment.extensionCount}
                fontSize={FontSizes.small}
              />
            </Space>
            {assignment.state === 'cancelled' && (
              <div style={{ marginTop: 8 }}>
                <AssignmentCancelReason
                  reasonName={assignment.cancelledReason}
                  details={assignment.cancelledDetail}
                />
              </div>
            )}
          </>
        );
      },
    },
  ];

  const initialColumnsKeys = selectColumnsStore.get(LOCAL_STORAGE_KEY) || [
    'name',
    'employeeId',
    'supplier',
    'externalId',
    'hiringLeader',
    'costCenter',
    'organizationBusiness',
    'clinicalUnit',
    'role',
    'shiftType',
    'shift',
    'rate',
    'employmentType',
    'startDate',
    'endDate',
    'state',
  ];

  const [selectedColumns, setSelectedColumns] = useState(initialColumnsKeys);

  const handleColumnChange = (checkedValues) => {
    setSelectedColumns(checkedValues);

    selectColumnsStore.set(LOCAL_STORAGE_KEY, checkedValues);

    if (filters) {
      const newFilters = checkedValues.reduce((acc, key) => {
        if (filters[key]) {
          acc[key] = filters[key];
        }
        return acc;
      }, {});

      filterChangeHandler(newFilters);
    }
  };

  const filteredColumns = columns.filter((col) =>
    selectedColumns.includes(`${col.key}`)
  );

  useEffect(() => {
    selectColumnsStore.set(LOCAL_STORAGE_KEY, selectedColumns);
  }, [selectedColumns]);

  const filtersDataReady =
    !!orgBusinessData &&
    !!supplierData &&
    !!rolesData &&
    !!clinicalUnitData &&
    !!employmentTypesData;

  const anyError =
    error ||
    orgBusinesserror ||
    clinicalUnitError ||
    supplierError ||
    rolesError ||
    employmentTypesError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="LongTermTeamTable" />;
  }

  const assignments =
    data?.paginatedStaffingRequestAssignments.staffingRequestAssignments || [];

  const total = data?.paginatedStaffingRequestAssignments.totalCount || null;

  if (data && !hasAnyFilter && assignments && assignments.length === 0) {
    return (
      <>
        <EmptyContent
          imagePath="./team/empty-team.png"
          header="So much empties... for now."
          text="Team Members will be added as soon as they accept an offer. Here, you'll be able to manage their assignments, compliance, and more."
        />
      </>
    );
  }

  return (
    <>
      <Portal
        targetId="wk-portal-target:organization-team-page-long-term-extra-actions"
        triggerRender={triggerPortal}
      >
        <SelectColumnsDropdown
          allColumns={columns}
          onChange={handleColumnChange}
          defaultColumnsKeys={initialColumnsKeys}
        />
      </Portal>

      <ExtensionOffersApprovalsPendingList columns={columns} />
      {showHeader && (
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <ButtonGroup>
              <ExportAssignmentsButton
                filterKey="ClientAdminTeamTable"
                requestTypes={['long_term']}
              />
              <SelectColumnsDropdown
                allColumns={columns}
                onChange={handleColumnChange}
                defaultColumnsKeys={initialColumnsKeys}
              />
            </ButtonGroup>
          </Col>
        </Row>
      )}
      <FilteredTable
        rowKey="id"
        columns={filteredColumns}
        dataSource={filteredColumns.length > 0 && assignments}
        loading={loading}
        filtersDataReady={filtersDataReady}
        pagination={{ ...paginationVariables, total }}
        onChange={tableChangeHandler}
        onFilterChange={(newFilters) => {
          filterChangeHandler(newFilters);
          enableExport();
        }}
        localStorageFilterKey={localStorageFilterKey}
        locale={{
          emptyText: filteredColumns.length === 0 && (
            <Empty description="Please select columns to populate the list" />
          ),
        }}
      />
      <style jsx global>{`
        .ant-table-row.non-compliant-row {
          color: ${Colors.coral};
          background-color: ${Colors.coralHyperlight};
        }

        .ant-table-row.non-compliant-row a {
          color: ${Colors.coral};
        }

        .ant-table-row.non-compliant-row td {
          background-color: ${Colors.coralHyperlight};
        }
      `}</style>
    </>
  );
}

LongTermTeamTable.propTypes = {
  showHeader: PropTypes.bool,
  triggerPortal: PropTypes.bool,
};
